// hooks/usePlayerBadges.js
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import axiosConn from '../axiosConn';

const LS_KEY = (id) => `jg.badges.${id}`;
const NOW = () => Date.now();

const classifyError = (e) => {
  if (!e) return 'Unknown error';
  const s = e.response?.status;
  if (s === 401) return 'Unauthorized';
  if (s === 403) return 'Forbidden';
  if (s === 404) return 'Not found';
  if (s === 304) return 'Not modified';
  if (e.message === 'canceled') return 'Cancelled';
  return e.message || 'Failed to load badges';
};

function normalizeEarned(
  earned = [],
  { dedupeStrategy = 'slugOrId', keep = 'first', sort = 'earnedAtDesc' } = {},
) {
  if (!Array.isArray(earned)) return [];
  const map = new Map();

  for (const raw of earned) {
    const b = raw.badge || {};
    const id = raw.badgeId || b._id || raw.id || raw._id;
    if (!id) continue;

    const slug = raw.slug || b.slug || null;
    const name = raw.name || b.name || '';
    const desc = raw.description ?? raw.desc ?? b.description ?? b.desc ?? '';
    const iconUrl =
      [
        raw.iconUrl,
        b.iconUrl,
        b.icon,
        raw.icon,
        raw.imageUrl,
        b.imageUrl,
        raw.image,
        b.image,
        raw.mediaUrl,
        b.mediaUrl,
        raw.assets?.icon,
        raw.assets?.imageUrl,
        b.assets?.icon,
        b.assets?.imageUrl,
      ].find((x) => typeof x === 'string' && x.trim()) || '';
    const type = raw.type || b.type || '';
    const metric = (raw.metric || b.metric || '').trim();
    const threshold = raw.threshold ?? b.threshold ?? null;
    const isActive = raw.isActive ?? b.isActive ?? true;
    const autoAward = raw.autoAward ?? b.autoAward ?? false;

    const earnedAtRaw =
      raw.earnedAt ||
      raw.awardedAt ||
      raw.completedAt ||
      raw.date ||
      b.earnedAt ||
      null;

    const progressObj = raw.progress || b.progress || undefined;
    const progressPercent =
      typeof raw.progressPercent === 'number'
        ? raw.progressPercent
        : typeof progressObj?.percent === 'number'
        ? progressObj.percent
        : undefined;

    const obj = {
      id,
      slug,
      name,
      desc,
      iconUrl,
      type,
      metric,
      threshold,
      isActive,
      autoAward,
      earnedAt: earnedAtRaw ? new Date(earnedAtRaw).toISOString() : null,
      progress: progressObj,
      progressPercent,
      globalEarnCount:
        typeof raw.globalEarnCount === 'number'
          ? raw.globalEarnCount
          : typeof b.globalEarnCount === 'number'
          ? b.globalEarnCount
          : undefined,
      rarityScore:
        typeof raw.rarityScore === 'number'
          ? raw.rarityScore
          : typeof b.rarityScore === 'number'
          ? b.rarityScore
          : undefined,
      raw,
    };

    // NEW: keep dailyVisit variants distinct by day/threshold
    const isDaily = (obj.metric || '').toLowerCase() === 'dailyvisit';
    const dayHint =
      obj.threshold ??
      (typeof obj.progress?.current === 'number' ? obj.progress.current : null);
    const discriminator = isDaily && dayHint != null ? `|day:${dayHint}` : '';

    const key =
      dedupeStrategy === 'badgeId'
        ? `${obj.id}${discriminator}`
        : dedupeStrategy === 'slug'
        ? `${obj.slug || obj.id}${discriminator}`
        : `${obj.slug || obj.id}${discriminator}`;

    if (!map.has(key)) {
      map.set(key, obj);
    } else {
      const existing = map.get(key);
      if (keep === 'earliest') {
        const a = existing.earnedAt
          ? new Date(existing.earnedAt).getTime()
          : -Infinity;
        const bTime = obj.earnedAt
          ? new Date(obj.earnedAt).getTime()
          : -Infinity;
        if (bTime < a) map.set(key, obj);
      } else {
        // treat 'first' as 'latest' because backend order may vary
        const a = existing.earnedAt
          ? new Date(existing.earnedAt).getTime()
          : -Infinity;
        const bTime = obj.earnedAt
          ? new Date(obj.earnedAt).getTime()
          : -Infinity;
        if (bTime > a) map.set(key, obj);
      }
    }
  }

  let out = Array.from(map.values());

  if (sort === 'earnedAtDesc') {
    out.sort(
      (a, b) =>
        (b.earnedAt ? new Date(b.earnedAt).getTime() : 0) -
        (a.earnedAt ? new Date(a.earnedAt).getTime() : 0),
    );
  } else if (sort === 'earnedAtAsc') {
    out.sort(
      (a, b) =>
        (a.earnedAt ? new Date(a.earnedAt).getTime() : 0) -
        (b.earnedAt ? new Date(b.earnedAt).getTime() : 0),
    );
  } else if (sort === 'nameAsc') {
    out.sort((a, b) => a.name.localeCompare(b.name));
  } else if (sort === 'rarestDesc') {
    out.sort((a, b) => (b.rarityScore ?? -1) - (a.rarityScore ?? -1));
  }

  return out;
}

export default function usePlayerBadges(
  playerId,
  {
    token,
    staleMs = 60_000,
    revalidateOnFocus = true,

    dedupeStrategy = 'slugOrId',
    duplicateKeep = 'first',
    sort = 'earnedAtDesc',

    includeRarity = true,
    minimal = false,
    group = '',
    limit = 500,
    skip = 0,
    includeLocked = false, // caller intent; may be coerced below
    product = '',
    types = '',
    categories = '',

    // NEW: who is viewing? If provided and differs from playerId, we’ll force public mode.
    viewerId = null,

    debug = false,
  } = {},
) {
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const cacheRef = useRef({});
  const abortRef = useRef(null);

  if (token && axiosConn?.defaults?.headers?.common) {
    const desired = `Bearer ${token}`;
    if (axiosConn.defaults.headers.common.Authorization !== desired) {
      axiosConn.defaults.headers.common.Authorization = desired;
    }
  }

  const readPersisted = useCallback(() => {
    if (!playerId) return null;
    try {
      const raw = localStorage.getItem(LS_KEY(playerId));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [playerId]);

  const cached = useMemo(() => {
    if (!playerId) return null;
    const mem = cacheRef.current[playerId];
    if (mem && NOW() - mem.t < staleMs) return mem;
    const persisted = readPersisted();
    if (persisted && NOW() - persisted.t < staleMs) return persisted;
    return null;
  }, [playerId, staleMs, readPersisted]);

  const commitCache = useCallback(
    (payload) => {
      if (!playerId) return;
      cacheRef.current[playerId] = payload;
      try {
        localStorage.setItem(LS_KEY(playerId), JSON.stringify(payload));
      } catch {}
    },
    [playerId],
  );

  const extractEarned = (respBody) => {
    const earnedRaw = Array.isArray(respBody?.earned)
      ? respBody.earned
      : Array.isArray(respBody?.data?.earned)
      ? respBody.data.earned
      : respBody?.earned && typeof respBody.earned === 'object'
      ? Object.values(respBody.earned).flat()
      : [];

    const totals = respBody?.totals ||
      respBody?.data?.totals || {
        earnedCount: earnedRaw.length,
        availableCount: null,
      };

    const etag = respBody?.etag || respBody?.data?.etag || null;

    return { earned: earnedRaw, meta: { ...totals, etag } };
  };

  const applyNormalization = useCallback(
    (rawResp, etagHint = null) => {
      const { earned, meta: metaIn } = extractEarned(rawResp);
      const norm = normalizeEarned(earned, {
        dedupeStrategy,
        keep: duplicateKeep,
        sort,
      });

      if (debug) {
        const dv = norm.filter(
          (x) => (x.metric || '').toLowerCase() === 'dailyvisit',
        );
        console.log(
          `[badges] normalized=${norm.length} (raw=${earned.length}) • dailyVisit=${dv.length}`,
          {
            sample: dv
              .slice(0, 3)
              .map((x) => ({ id: x.id, name: x.name, slug: x.slug })),
          },
        );
      }

      const payload = {
        t: NOW(),
        badges: norm,
        meta: {
          ...metaIn,
          etag: metaIn.etag || etagHint || null,
          originalCount: earned.length,
          normalizedCount: norm.length,
        },
      };
      commitCache(payload);
      setData(norm);
      setMeta(payload.meta);
      setStatus('success');
    },
    [dedupeStrategy, duplicateKeep, sort, commitCache, debug],
  );

  const fetchOnce = useCallback(
    async (opts, { background = false } = {}) => {
      const {
        playerId,
        includeLockedEff,
        includeRarity,
        minimal,
        group,
        limit,
        skip,
        product,
        types,
        categories,
      } = opts;

      const controller = new AbortController();
      abortRef.current = controller;

      if (!background) {
        if (!cached) setStatus('loading');
        setError(null);
      } else {
        setRefreshing(true);
      }

      try {
        const qs = new URLSearchParams({
          includeRarity: String(includeRarity),
          minimal: String(minimal),
          limit: String(Math.min(Math.max(limit, 1), 1000)),
          skip: String(Math.max(skip, 0)),
          includeLocked: String(includeLockedEff),
        });
        if (group) qs.set('group', group);
        if (product !== '') qs.set('product', product);
        if (types !== '') qs.set('types', types);
        if (categories !== '') qs.set('categories', categories);

        const url = `players/${playerId}/badges?${qs.toString()}`;

        const ifNoneMatch =
          cached?.meta?.etag || cacheRef.current[playerId]?.meta?.etag;

        const headers = ifNoneMatch ? { 'If-None-Match': ifNoneMatch } : {};

        if (debug) console.log('[badges] GET', url, { headers });

        const resp = await axiosConn.get(url, {
          signal: controller.signal,
          headers,
          validateStatus: () => true,
        });

        if (resp.status === 304 && cached) {
          if (debug) console.log('[badges] 304 Not Modified — cache hit');
          setData(cached.badges);
          setMeta(cached.meta);
          setStatus('success');
          return { ok: true, status: 304 };
        }

        if (resp.status >= 200 && resp.status < 300) {
          const etagHdr =
            resp.headers?.etag || resp.headers?.ETag || resp.data?.etag || null;

          if (debug) {
            const { earned } = extractEarned(resp.data);
            const dvRaw = earned.filter(
              (b) => (b.metric || '').toLowerCase() === 'dailyvisit',
            );
            console.log(
              `[badges] 200 OK raw earned=${earned.length} • raw dailyVisit=${dvRaw.length}`,
            );
          }

          applyNormalization(resp.data, etagHdr);
          return { ok: true, status: resp.status };
        }

        // soft error path
        const msg =
          resp?.data?.message || resp?.data?.error || `HTTP ${resp.status}`;
        if (!cached) {
          setStatus('error');
          setError(msg);
        } else {
          console.warn('[badges] soft error (cached retained):', msg);
        }
        return { ok: false, status: resp.status, msg };
      } catch (e) {
        if (controller.signal.aborted) return { ok: false, aborted: true };
        const code = e?.response?.status;
        if (code === 304 && cached) {
          setData(cached.badges);
          setMeta(cached.meta);
          setStatus('success');
          return { ok: true, status: 304 };
        }
        const msg = classifyError(e);
        if (!cached) {
          setStatus('error');
          setError(msg);
        } else {
          console.warn('[badges] soft error (cached retained):', msg);
        }
        return { ok: false, msg };
      } finally {
        if (background) setRefreshing(false);
      }
    },
    [cached, applyNormalization, debug],
  );

  const fetchBadges = useCallback(
    async ({ background = false } = {}) => {
      if (!playerId) return;

      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {}
      }

      // Coerce includeLocked if viewer != subject
      const isSelfView =
        viewerId && String(viewerId) === String(playerId) ? true : false;
      const includeLockedEff = isSelfView ? includeLocked : false;

      // First attempt
      const first = await fetchOnce(
        {
          playerId,
          includeLockedEff,
          includeRarity,
          minimal,
          group,
          limit,
          skip,
          product,
          types,
          categories,
        },
        { background },
      );

      // Auto-retry once WITHOUT locked if server said Unauthorized/Forbidden
      if (
        first &&
        !first.ok &&
        (first.status === 401 || first.status === 403) &&
        includeLockedEff
      ) {
        if (debug)
          console.log(
            '[badges] retrying without includeLocked due to auth error…',
          );
        await fetchOnce(
          {
            playerId,
            includeLockedEff: false,
            includeRarity,
            minimal,
            group,
            limit,
            skip,
            product,
            types,
            categories,
          },
          { background },
        );
      }
    },
    [
      playerId,
      viewerId,
      includeLocked,
      includeRarity,
      minimal,
      group,
      limit,
      skip,
      product,
      types,
      categories,
      fetchOnce,
      debug,
    ],
  );

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!playerId) return;
      if (cached) {
        setData(cached.badges);
        setMeta(cached.meta);
        setStatus('success');
      }
      await fetchBadges({ background: false });
      if (!mounted) return;
    })();
    return () => {
      mounted = false;
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {}
      }
    };
  }, [playerId, fetchBadges, cached]);

  useEffect(() => {
    if (!revalidateOnFocus) return;
    const onFocus = () => {
      if (!playerId) return;
      const mem = cacheRef.current[playerId];
      if (!mem || NOW() - mem.t >= staleMs) {
        fetchBadges({ background: true });
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [playerId, revalidateOnFocus, fetchBadges, staleMs]);

  const refetch = useCallback(
    () => fetchBadges({ background: true }),
    [fetchBadges],
  );

  return {
    badges: data || [],
    meta, // { earnedCount, availableCount, etag, originalCount, normalizedCount }
    status, // 'idle' | 'loading' | 'success' | 'error'
    error, // string | null
    refetch, // () => void
    refreshing, // boolean
  };
}
